import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { DashboardLayout } from '../../components/DashboardLayout';
import { BarcodeScanner } from '../../components/BarcodeScanner';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { Zap, Keyboard, Camera, Undo2, CheckCircle2, FileDown, AlertTriangle, ArrowLeft, Trash2, RotateCcw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}` } });
const uuid = () => (crypto?.randomUUID ? crypto.randomUUID() : 'x' + Date.now() + Math.random());
const eur = (c) => (c == null ? '—' : (c / 100).toFixed(2).replace('.', ',') + ' €');
const parseEur = (s) => {
  let str = String(s).trim();
  if (str.includes(',')) str = str.replace(/\./g, '').replace(',', '.'); // Komma = Dezimal, Punkt = Tausender
  const n = parseFloat(str);                                             // sonst Punkt als Dezimal akzeptieren
  if (isNaN(n) || n < 0) return null;
  return Math.round(n * 100);
};

// Sichtbare Inventur-UI-Versionskennung (zur eindeutigen Identifikation des ausgelieferten Stands)
const INV_UI_VERSION = 'inv-ui-2026-10-29d';

// App-eigenes Ziffernfeld – funktioniert unabhängig von der iOS-Bildschirmtastatur.
// Schreibt ausschließlich in den Dialog-State; löst KEINE OS-Tastatur und KEINE Scan-Zählung aus.
const Keypad = ({ kind, onKey, onBackspace, onClear, testid }) => {
  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', kind === 'decimal' ? ',' : '', '0', 'back'];
  return (
    <div className="mt-2" data-testid={testid}>
      <div className="grid grid-cols-3 gap-2">
        {keys.map((k, i) => {
          if (k === '') return <span key={i} />;
          if (k === 'back') return (
            <button key={i} type="button" data-testid={`${testid}-back`} onClick={onBackspace}
              className="py-3 rounded-lg border border-border bg-muted/50 text-foreground font-semibold text-lg active:bg-muted">⌫</button>
          );
          return (
            <button key={i} type="button" data-testid={`${testid}-k-${k === ',' ? 'dec' : k}`} onClick={() => onKey(k)}
              className="py-3 rounded-lg border border-border bg-background text-foreground font-semibold text-lg active:bg-muted">{k}</button>
          );
        })}
      </div>
      <button type="button" data-testid={`${testid}-clear`} onClick={onClear}
        className="w-full mt-2 py-2 rounded-lg border border-border text-muted-foreground text-sm active:bg-muted">Löschen</button>
    </div>
  );
};

export default function PressInventoryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [inv, setInv] = useState(null);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [mode, setMode] = useState('quick');        // 'quick' | 'manual'
  const [loading, setLoading] = useState(true);
  const [soundOn, setSoundOn] = useState(true);
  const [lastFlash, setLastFlash] = useState(null);  // {type, text}
  const [showCamera, setShowCamera] = useState(false);
  const [dialog, setDialog] = useState(null);         // unresolved/manual dialog state
  const [submitting, setSubmitting] = useState(false);
  const [keypadTarget, setKeypadTarget] = useState('vk'); // 'vk' | 'raw' | null – welches Feld das App-Ziffernfeld füllt
  const [delEntry, setDelEntry] = useState(null);     // Lösch-Bestätigung für beliebige Erfassung
  const [deleting, setDeleting] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false); // Bestätigung „wieder öffnen"
  const [reopening, setReopening] = useState(false);
  const [locations, setLocations] = useState([]);
  const scanInputRef = useRef(null);
  const captureActive = useRef(true);
  const processingRef = useRef(false);
  const scanQueueRef = useRef([]);
  const focusTimerRef = useRef(null);                 // offener Refokus-Timer, sauber abbrechbar
  const dialogOpenRef = useRef(false);                // true solange irgendein Dialog offen ist
  const cancelFocusTimer = () => { if (focusTimerRef.current) { clearTimeout(focusTimerRef.current); focusTimerRef.current = null; } };

  const lsKey = user ? `pressinv:${user.customer_id}:${user.id}:${id}` : null;

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/press-inventory/${id}`, authHeader());
      setInv(res.data.inventory);
      setEntries(res.data.entries || []);
      setSummary(res.data.summary);
    } catch (e) {
      toast.error('Inventur konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/api/user/locations`, authHeader()).then(r => setLocations(r.data || [])).catch(() => {});
  }, []);
  const locLabel = (id) => (locations.find(l => l.id === id) || {}).name || id || 'ohne Standort';

  // ── Pending-Puffer (überlebt Refresh/kurzen Netzverlust) ──
  const getQueue = () => {
    if (!lsKey) return [];
    try { return JSON.parse(localStorage.getItem(lsKey) || '[]'); }
    catch { return []; }
  };
  const setQueue = (q) => {
    if (!lsKey) { toast.error('Lokaler Speicher nicht verfügbar – bitte online bleiben'); return; }
    try { localStorage.setItem(lsKey, JSON.stringify(q)); }
    catch { toast.error('Lokaler Speicher nicht verfügbar'); }
  };

  const beep = () => {
    if (!soundOn) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination); o.frequency.value = 880; g.gain.value = 0.05;
      o.start(); setTimeout(() => { o.stop(); ctx.close(); }, 90);
    } catch { /* ignore */ }
  };

  // Scanner-Refokus: läuft NIE während ein Dialog offen ist; offener Timer wird vorher abgebrochen.
  const refocus = () => {
    cancelFocusTimer();
    if (mode === 'quick' && captureActive.current && !dialogOpenRef.current && scanInputRef.current) {
      focusTimerRef.current = setTimeout(() => {
        focusTimerRef.current = null;
        if (captureActive.current && !dialogOpenRef.current && scanInputRef.current) scanInputRef.current.focus();
      }, 10);
    }
  };
  useEffect(() => { refocus(); }, [mode, entries]);
  useEffect(() => { const open = !!(dialog || delEntry); dialogOpenRef.current = open; if (open) cancelFocusTimer(); }, [dialog, delEntry]);
  useEffect(() => () => cancelFocusTimer(), []); // Cleanup beim Unmount
  // Escape bricht die Lösch-Bestätigung ab (sichere Abbrechen-Aktion)
  useEffect(() => {
    if (!delEntry) return;
    const onKey = (e) => { if (e.key === 'Escape') closeDeleteDialog(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line
  }, [delEntry]);

  const postEntry = async (payload) => {
    // in Queue aufnehmen (für Retry bei Netzverlust)
    const q = getQueue(); q.push(payload); setQueue(q);
    try {
      const res = await axios.post(`${API}/api/press-inventory/${id}/entries`, payload, authHeader());
      const remaining = getQueue().filter(p => p.client_entry_id !== payload.client_entry_id);
      setQueue(remaining);
      const entry = res.data.entry;
      setEntries(prev => (prev.some(e => e.id === entry.id) ? prev : [entry, ...prev]));
      await load();
      if (entry.status === 'unresolved') {
        setLastFlash({ type: 'check', text: `Prüfen: ${entry.raw_scan || ''}` });
        openResolveDialog(entry);
      } else {
        beep();
        setLastFlash({ type: 'ok', text: `${eur(entry.vk_cents)} · ${entry.tax_rate}% · +${entry.qty}` });
      }
      return entry;
    } catch (e) {
      if (e?.response) {
        // Serverfehler (4xx/5xx) → nicht endlos wiederholen
        setQueue(getQueue().filter(p => p.client_entry_id !== payload.client_entry_id));
        setLastFlash({ type: 'err', text: e.response?.data?.detail || 'Erfassung abgelehnt' });
        toast.error(e.response?.data?.detail || 'Erfassung abgelehnt');
      } else {
        // Netzfehler → bleibt in Queue für späteren Retry
        setLastFlash({ type: 'err', text: 'Netzproblem – bleibt lokal ausstehend' });
        toast.error('Netzproblem – Scan bleibt lokal ausstehend');
      }
      return null;
    }
  };

  const flushQueue = async () => {
    const q = getQueue();
    for (const payload of q) {
      try {
        await axios.post(`${API}/api/press-inventory/${id}/entries`, payload, authHeader());
        setQueue(getQueue().filter(p => p.client_entry_id !== payload.client_entry_id));
      } catch { /* bleibt in Queue */ }
    }
    await load();
  };
  useEffect(() => { if (!loading && inv?.status === 'draft') flushQueue(); /* eslint-disable-next-line */ }, [loading]);

  // ── Schnellscan: HID-Scanner liefert Code + Enter/Tab ──
  // Scans werden SERIELL verarbeitet: solange ein Scan in Verarbeitung ist (oder der
  // STOP-Dialog offen ist), wird der nächste Code gepuffert, nicht sofort gezählt.
  const processQueue = async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (scanQueueRef.current.length && captureActive.current) {
        const raw = scanQueueRef.current.shift();
        await postEntry({ client_entry_id: uuid(), source: 'scanner', raw_scan: raw, qty: 1, client_seq: Date.now() });
        if (!captureActive.current) break; // unresolved -> Dialog offen: Rest bleibt gepuffert
      }
    } finally {
      processingRef.current = false;
      refocus();
    }
  };

  const handleScanKey = async (e) => {
    if (!captureActive.current) { e.preventDefault(); return; } // STOP: Dialog offen → ignorieren
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      const raw = e.currentTarget.value.trim();
      e.currentTarget.value = '';
      if (!raw || raw.length < 3) return;   // einzelne Ziffern nicht als Code werten
      scanQueueRef.current.push(raw);
      processQueue();
    }
  };

  const openResolveDialog = (entry) => {
    captureActive.current = false;
    cancelFocusTimer();
    if (scanInputRef.current) { scanInputRef.current.value = ''; scanInputRef.current.blur(); }
    setKeypadTarget('vk');
    setDialog({
      entryId: entry.id, raw: entry.raw_scan, reason: entry.decoded?.reason || 'Nicht automatisch erkannt',
      vk: entry.vk_cents != null ? (entry.vk_cents / 100).toFixed(2).replace('.', ',') : '',
      tax: entry.tax_rate || '', mode: 'resolve',
    });
  };

  const openManualDialog = () => {
    captureActive.current = false;
    cancelFocusTimer();
    if (scanInputRef.current) { scanInputRef.current.value = ''; scanInputRef.current.blur(); }
    setKeypadTarget('vk');
    setDialog({ mode: 'manual', raw: '', vk: '', tax: '', qty: 1 });
  };

  const closeDialog = () => {
    setDialog(null);
    dialogOpenRef.current = false;   // sofort setzen, damit refocus() synchron greift (Effect läuft erst nach Render)
    setKeypadTarget('vk');
    captureActive.current = (mode === 'quick');
    if (scanInputRef.current) scanInputRef.current.value = '';
    refocus();
    if (captureActive.current) processQueue(); // gepufferte Scans fortsetzen
  };

  const submitDialog = async () => {
    if (submitting) return;
    const vk_cents = parseEur(dialog.vk);
    if (vk_cents == null) { toast.error('Bitte gültigen VK eingeben'); return; }
    if (![7, 19].includes(Number(dialog.tax))) { toast.error('Bitte Steuersatz 7 % oder 19 % wählen'); return; }
    setSubmitting(true);
    try {
      if (dialog.mode === 'resolve') {
        await axios.patch(`${API}/api/press-inventory/${id}/entries/${dialog.entryId}`,
          { vk_cents, tax_rate: Number(dialog.tax) }, authHeader());
        toast.success('Erfassung korrigiert');
        setLastFlash({ type: 'ok', text: `Geklärt: ${eur(vk_cents)} · ${dialog.tax}%` });
        closeDialog();
        await load();
      } else {
        const qty = Math.max(1, parseInt(dialog.qty || 1, 10));
        const entry = await postEntry({
          client_entry_id: uuid(), source: 'manual',
          raw_scan: dialog.raw || null, vk_cents, tax_rate: Number(dialog.tax), qty, client_seq: Date.now(),
        });
        if (!entry) return;                         // Serverfehler/Netz → Dialog offen lassen, Eingabe bleibt erhalten
        if (entry.status === 'unresolved') return;  // postEntry hat bereits den Klären-Dialog geöffnet
        closeDialog();                              // postEntry hat bereits geladen
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Übernahme fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  // Barcode im Modus „Scan + Preis": Vorschlag holen (zählt NICHT automatisch)
  const parsePrefill = async (raw) => {
    const code = (raw ?? dialog?.raw ?? '').trim();
    if (code.length < 3) return;
    try {
      const r = (await axios.post(`${API}/api/press-inventory/parse`, { raw_scan: code }, authHeader())).data;
      setDialog(d => ({
        ...d, raw: code,
        vk: r.vk_cents != null ? (r.vk_cents / 100).toFixed(2).replace('.', ',') : d.vk,
        tax: r.tax_rate || d.tax,
        reason: r.decode_status === 'decoded' ? 'Automatisch erkannt' : (r.reason || 'Nicht automatisch erkannt – bitte Preis/Steuer prüfen'),
      }));
    } catch { /* ignore */ }
  };

  // ── App-Ziffernfeld-Handler (unabhängig von OS-Tastatur) ──
  const vkKey = (k) => setDialog(d => {
    if (!d) return d;
    let v = d.vk || '';
    if (k === ',') { if (v.includes(',')) return d; return { ...d, vk: (v === '' ? '0' : v) + ',' }; }
    if (v.includes(',') && v.split(',')[1].length >= 2) return d; // max. 2 Nachkommastellen
    return { ...d, vk: v + k };
  });
  const vkBack = () => setDialog(d => (d ? { ...d, vk: (d.vk || '').slice(0, -1) } : d));
  const vkClear = () => setDialog(d => (d ? { ...d, vk: '' } : d));
  const rawKey = (k) => setDialog(d => (d ? { ...d, raw: (d.raw || '') + k } : d));
  const rawBack = () => setDialog(d => (d ? { ...d, raw: (d.raw || '').slice(0, -1) } : d));
  const rawClear = () => setDialog(d => (d ? { ...d, raw: '' } : d));
  const qtyStep = (delta) => setDialog(d => (d ? { ...d, qty: Math.max(1, (parseInt(d.qty || 1, 10) || 1) + delta) } : d));

  const undoLast = async () => {
    const active = entries.find(e => !e.voided);
    if (!active) { toast.info('Nichts zum Rückgängigmachen'); return; }
    try {
      await axios.post(`${API}/api/press-inventory/${id}/entries/${active.id}/void`, {}, authHeader());
      toast.success('Letzte Erfassung rückgängig gemacht');
      await load();
    } catch { toast.error('Rückgängig fehlgeschlagen'); }
  };

  // ── Beliebige Erfassung aus der Historie stornieren (Papierkorb + Bestätigung) ──
  const openDeleteDialog = (entry) => {
    captureActive.current = false;
    cancelFocusTimer();
    if (scanInputRef.current) { scanInputRef.current.value = ''; scanInputRef.current.blur(); }
    setDelEntry(entry);
  };
  const closeDeleteDialog = () => {
    if (deleting) return;                    // Schließen/Escape während laufendem Request ignorieren
    setDelEntry(null);
    dialogOpenRef.current = false;           // synchron, damit refocus() greift
    captureActive.current = (mode === 'quick');
    if (scanInputRef.current) scanInputRef.current.value = '';
    refocus();
    if (captureActive.current) processQueue();
  };
  const confirmDelete = async () => {
    if (deleting || !delEntry) return;       // Doppelbetätigung verhindern
    setDeleting(true);
    try {
      await axios.post(`${API}/api/press-inventory/${id}/entries/${delEntry.id}/void`,
        { reason: 'deleted' }, authHeader());
      toast.success('Erfassung gelöscht');
      setDelEntry(null);
      dialogOpenRef.current = false;
      captureActive.current = (mode === 'quick');
      await load();                          // Historie, Stückzahl, Summen, Export-Grundlage konsistent
      refocus();
      if (captureActive.current) processQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Löschen fehlgeschlagen'); // Zeile & Summen bleiben erhalten
    } finally {
      setDeleting(false);
    }
  };

  const onCameraDetected = async (code) => {
    setShowCamera(false);
    if (mode === 'manual') {
      // In (ggf. offenen) manuellen Dialog übernehmen + Vorschlag holen; zählt NICHT automatisch
      setDialog(d => ({ ...(d && d.mode === 'manual' ? d : { mode: 'manual', vk: '', tax: '', qty: 1 }), raw: code }));
      captureActive.current = false;
      await parsePrefill(code);
    } else {
      captureActive.current = true;
      await postEntry({ client_entry_id: uuid(), source: 'camera', raw_scan: code, qty: 1, client_seq: Date.now() });
    }
  };

  const finalize = async () => {
    try {
      await axios.post(`${API}/api/press-inventory/${id}/finalize`, {}, authHeader());
      toast.success('Inventur abgeschlossen');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Abschluss fehlgeschlagen'); }
  };

  const confirmReopen = async () => {
    if (reopening) return;                       // Doppel-/Parallelklick verhindern
    setReopening(true);
    try {
      await axios.post(`${API}/api/press-inventory/${id}/reopen`, {}, authHeader());
      toast.success('Inventur zur Bearbeitung wieder geöffnet');
      setReopenOpen(false);
      await load();                              // Status/Erfassungen/Summen frisch laden (unverändert)
    } catch (e) { toast.error(e?.response?.data?.detail || 'Wiederöffnen fehlgeschlagen'); }
    finally { setReopening(false); }
  };

  const download = async (fmt) => {
    try {
      const res = await axios.get(`${API}/api/press-inventory/${id}/export.${fmt}`,
        { ...authHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = `presse_inventur_${id.slice(0, 8)}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error('Export fehlgeschlagen'); }
  };

  if (loading) return <DashboardLayout><div className="p-8 text-muted-foreground">Lädt…</div></DashboardLayout>;
  if (!inv) return <DashboardLayout><div className="p-8">Nicht gefunden</div></DashboardLayout>;

  const isDraft = inv.status === 'draft';
  const canReopen = !!(user && (user.role === 'admin' || user.role === 'superadmin' || user.press_reopen_permission === true));
  const visibleEntries = entries;
  const pendingCount = getQueue().length;

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-5" data-testid="press-inventory-detail">
        <button onClick={() => navigate('/inventur/presse')} className="text-sm text-muted-foreground flex items-center gap-1 hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Zur Übersicht
        </button>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-foreground">Presse-Inventur · {inv.inventory_date}</h1>
            <p className="text-sm text-muted-foreground">
              {locLabel(inv.location_id)} · Status: {isDraft ? 'Entwurf' : 'Abgeschlossen'}
              {pendingCount > 0 && <span className="ml-2 text-amber-600">· {pendingCount} lokal ausstehend</span>}
              <span className="ml-2 text-[11px] text-muted-foreground/70" data-testid="inv-ui-version">UI {INV_UI_VERSION}</span>
            </p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="export-pdf-btn" variant="outline" size="sm" className="gap-1" onClick={() => download('pdf')}><FileDown className="h-4 w-4" /> PDF</Button>
            <Button data-testid="export-csv-btn" variant="outline" size="sm" className="gap-1" onClick={() => download('csv')}><FileDown className="h-4 w-4" /> CSV</Button>
            {isDraft && <Button data-testid="finalize-btn" size="sm" className="gap-1 bg-emerald-600 hover:bg-emerald-700" onClick={finalize}><CheckCircle2 className="h-4 w-4" /> Abschließen</Button>}
            {!isDraft && canReopen && (
              <Button data-testid="reopen-btn" size="sm" variant="outline" className="gap-1 border-amber-500 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/20" onClick={() => setReopenOpen(true)}><RotateCcw className="h-4 w-4" /> Zur Bearbeitung wieder öffnen</Button>
            )}
          </div>
        </div>

        {/* Summen immer sichtbar */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="press-totals">
            <Stat label="Exemplare" value={summary.total_qty} />
            <Stat label="Gesamt Brutto" value={eur(summary.total_brutto_cents)} />
            <Stat label="7 % Brutto" value={eur((summary.tax_totals.find(t => t.tax_rate === 7) || {}).brutto_cents || 0)} />
            <Stat label="19 % Brutto" value={eur((summary.tax_totals.find(t => t.tax_rate === 19) || {}).brutto_cents || 0)} />
          </div>
        )}
        {summary?.unresolved_qty > 0 && (
          <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 rounded-lg px-3 py-2">
            <AlertTriangle className="h-4 w-4" /> {summary.unresolved_qty} ungeklärte Erfassung(en) – vor Abschluss klären
          </div>
        )}

        {isDraft && (
          <div className="bg-card border border-border rounded-2xl p-4 space-y-4">
            {/* Modus-Umschalter */}
            <div className="flex gap-2">
              <Button data-testid="mode-quick" size="sm" variant={mode === 'quick' ? 'default' : 'outline'} className="gap-1"
                onClick={() => { setMode('quick'); captureActive.current = true; }}><Zap className="h-4 w-4" /> Schnellscan</Button>
              <Button data-testid="mode-manual" size="sm" variant={mode === 'manual' ? 'default' : 'outline'} className="gap-1"
                onClick={() => { setMode('manual'); captureActive.current = false; }}><Keyboard className="h-4 w-4" /> Scan + Preis</Button>
              <button onClick={() => setSoundOn(s => !s)} className="ml-auto text-xs text-muted-foreground">Ton: {soundOn ? 'an' : 'aus'}</button>
            </div>

            {/* Letzter Scan / Flash */}
            {lastFlash && (
              <div data-testid="scan-flash" className={`rounded-xl px-4 py-3 text-center font-semibold ${
                lastFlash.type === 'ok' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                : lastFlash.type === 'check' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'}`}>
                {lastFlash.text}
              </div>
            )}

            {mode === 'quick' ? (
              <div>
                <label className="text-xs text-muted-foreground">Scanner-Feld aktiv – einfach scannen (tak tak tak)</label>
                <input ref={scanInputRef} data-testid="quick-scan-input" autoFocus onKeyDown={handleScanKey}
                  onBlur={refocus} placeholder="Bereit für Scanner…"
                  className="w-full mt-1 px-3 py-3 rounded-lg border-2 border-indigo-400 bg-background text-foreground text-lg" />
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button variant="outline" size="sm" className="gap-1" onClick={() => { captureActive.current = false; setShowCamera(true); }}><Camera className="h-4 w-4" /> Kamera-Einzelscan</Button>
                  <Button data-testid="undo-btn" variant="outline" size="sm" className="gap-1" onClick={undoLast}><Undo2 className="h-4 w-4" /> Letzte rückgängig</Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button data-testid="manual-open-btn" size="sm" className="gap-1" onClick={openManualDialog}><Keyboard className="h-4 w-4" /> Erfassung eingeben</Button>
                <Button variant="outline" size="sm" className="gap-1" onClick={() => { captureActive.current = false; setShowCamera(true); }}><Camera className="h-4 w-4" /> Kamera-Einzelscan</Button>
                <Button data-testid="undo-btn2" variant="outline" size="sm" className="gap-1" onClick={undoLast}><Undo2 className="h-4 w-4" /> Letzte rückgängig</Button>
              </div>
            )}
          </div>
        )}

        {/* Gruppen */}
        {summary && summary.groups.length > 0 && (
          <div className="bg-card border border-border rounded-2xl p-4" data-testid="press-groups">
            <h2 className="font-semibold text-foreground mb-2">Zusammenfassung (VK + Steuersatz)</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-muted-foreground"><th className="py-1">VK</th><th>Steuersatz</th><th>Stück</th><th>Brutto</th></tr></thead>
                <tbody>
                  {summary.groups.map(g => (
                    <tr key={`${g.vk_cents}_${g.tax_rate}`} className="border-t border-border">
                      <td className="py-1">{eur(g.vk_cents)}</td><td>{g.tax_rate}%</td><td>{g.qty}</td><td>{eur(g.brutto_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Einzelhistorie */}
        <div className="bg-card border border-border rounded-2xl p-4" data-testid="press-history">
          <h2 className="font-semibold text-foreground mb-2">Einzelhistorie (neueste zuerst)</h2>
          {visibleEntries.length === 0 ? <p className="text-sm text-muted-foreground">Noch keine Erfassungen</p> : (
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {visibleEntries.map(e => {
                const label = e.voided
                  ? (e.void_reason === 'corrected' ? 'ersetzt' : e.void_reason === 'deleted' ? 'gelöscht' : 'rückgängig')
                  : (e.status === 'unresolved' ? 'prüfen' : 'gespeichert');
                const color = e.voided ? 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                  : (e.status === 'unresolved' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                    : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300');
                return (
                <div key={e.id} data-testid={`history-row-${e.id}`} className={`flex items-center justify-between text-sm px-3 py-2 rounded-lg ${e.voided ? 'opacity-60' : 'bg-muted/40'}`}>
                  <div className={e.voided ? 'line-through' : ''}>
                    <span className="font-medium text-foreground">{eur(e.vk_cents)}</span>
                    <span className="text-muted-foreground"> · {e.tax_rate ? e.tax_rate + '%' : 'Steuer?'} · {e.qty}×</span>
                    <span className="text-xs text-muted-foreground ml-2">{e.source}{e.raw_scan ? ` · ${e.raw_scan}` : ''}{e.corrected_from ? ' · Korrektur' : ''}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span data-testid={`history-status-${e.id}`} className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${color}`}>{label}</span>
                    {isDraft && e.status === 'unresolved' && !e.voided && (
                      <button data-testid={`resolve-row-${e.id}`} onClick={() => openResolveDialog(e)}
                        className="text-xs px-2 py-1 rounded bg-amber-500 text-white hover:bg-amber-600">Klären</button>
                    )}
                    {isDraft && !e.voided && (
                      <button data-testid={`delete-row-${e.id}`} onClick={() => openDeleteDialog(e)}
                        aria-label="Erfassung löschen" title="Erfassung löschen"
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showCamera && <BarcodeScanner onDetected={onCameraDetected} onClose={() => { setShowCamera(false); captureActive.current = (mode === 'quick'); refocus(); }} />}

      {/* STOP / Korrektur-Dialog */}
      {dialog && (
        <div className="fixed inset-0 z-[10000] bg-black/50 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="resolve-dialog">
          <div className="bg-card rounded-2xl shadow-2xl w-full max-w-sm my-auto max-h-[90dvh] overflow-y-auto p-5 space-y-3">
            <h3 className="font-bold text-foreground">{dialog.mode === 'resolve' ? 'Scan prüfen' : 'Erfassung eingeben'}</h3>
            {dialog.mode === 'manual' && (
              <div>
                <label className="text-xs text-muted-foreground">Barcode / EAN (scannen, tippen oder Ziffernfeld – optional)</label>
                <div className="flex gap-2 mt-1">
                  <input data-testid="dialog-barcode" value={dialog.raw || ''}
                    inputMode="numeric"
                    onFocus={() => setKeypadTarget('raw')}
                    onChange={e => setDialog({ ...dialog, raw: e.target.value })}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); parsePrefill(e.currentTarget.value); } }}
                    placeholder="EAN scannen/eingeben" className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-border bg-background text-foreground" />
                  <Button data-testid="dialog-parse-btn" type="button" variant="outline" size="sm" className="shrink-0" onClick={() => parsePrefill()}>Erkennen</Button>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <button type="button" data-testid="keypad-target-raw" onClick={() => setKeypadTarget('raw')}
                    className={`text-xs flex items-center gap-1 ${keypadTarget === 'raw' ? 'text-indigo-600 font-medium' : 'text-muted-foreground'}`}>
                    <Keyboard className="h-3 w-3" /> Ziffernfeld für EAN
                  </button>
                  <button type="button" data-testid="dialog-camera-btn" onClick={() => { setShowCamera(true); }}
                    className="text-xs text-indigo-600 flex items-center gap-1"><Camera className="h-3 w-3" /> Mit Kamera scannen</button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">Ein Scan hier zählt erst mit „Übernehmen" – nicht automatisch. Ohne Barcode ebenfalls möglich.</p>
              </div>
            )}
            {dialog.mode === 'resolve' && dialog.raw && <p className="text-xs text-muted-foreground break-all">Rohcode: {dialog.raw}</p>}
            {dialog.reason && <p className={`text-xs ${dialog.reason === 'Automatisch erkannt' ? 'text-emerald-600' : 'text-amber-600'}`}>{dialog.reason}</p>}
            <div>
              <label className="text-xs text-muted-foreground">Verkaufspreis (€)</label>
              <input data-testid="dialog-vk" value={dialog.vk}
                onFocus={() => setKeypadTarget('vk')}
                onChange={e => setDialog({ ...dialog, vk: e.target.value })}
                inputMode="decimal" placeholder="z. B. 3,50" className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground" />
              <button type="button" data-testid="keypad-target-vk" onClick={() => setKeypadTarget('vk')}
                className={`mt-2 text-xs flex items-center gap-1 ${keypadTarget === 'vk' ? 'text-indigo-600 font-medium' : 'text-muted-foreground'}`}>
                <Keyboard className="h-3 w-3" /> Ziffernfeld für Preis
              </button>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Steuersatz</label>
              <div className="flex gap-2 mt-1">
                {[7, 19].map(r => (
                  <button key={r} data-testid={`dialog-tax-${r}`} onClick={() => setDialog({ ...dialog, tax: r })}
                    className={`flex-1 py-2 rounded-lg border ${Number(dialog.tax) === r ? 'bg-indigo-600 text-white border-indigo-600' : 'border-border text-foreground'}`}>{r}%</button>
                ))}
              </div>
            </div>
            {dialog.mode === 'manual' && (
              <div>
                <label className="text-xs text-muted-foreground">Menge</label>
                <div className="flex items-center gap-2 mt-1">
                  <button type="button" data-testid="dialog-qty-minus" onClick={() => qtyStep(-1)}
                    className="h-10 w-10 rounded-lg border border-border text-foreground text-lg font-semibold active:bg-muted">−</button>
                  <input data-testid="dialog-qty" type="number" min="1" inputMode="numeric" value={dialog.qty}
                    onChange={e => setDialog({ ...dialog, qty: e.target.value })}
                    className="flex-1 text-center px-3 py-2 rounded-lg border border-border bg-background text-foreground" />
                  <button type="button" data-testid="dialog-qty-plus" onClick={() => qtyStep(1)}
                    className="h-10 w-10 rounded-lg border border-border text-foreground text-lg font-semibold active:bg-muted">+</button>
                </div>
              </div>
            )}
            {keypadTarget && (
              <div className="rounded-lg border border-indigo-200 dark:border-indigo-900 bg-indigo-50/60 dark:bg-indigo-950/20 p-2" data-testid="inv-keypad-wrap">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">
                    Ziffernfeld: {keypadTarget === 'vk' ? 'Verkaufspreis (€)' : 'Barcode / EAN'}
                  </span>
                  <button type="button" data-testid="keypad-close" onClick={() => setKeypadTarget(null)}
                    className="text-xs text-muted-foreground">ausblenden</button>
                </div>
                <Keypad kind={keypadTarget === 'vk' ? 'decimal' : 'digits'}
                  onKey={keypadTarget === 'vk' ? vkKey : rawKey}
                  onBackspace={keypadTarget === 'vk' ? vkBack : rawBack}
                  onClear={keypadTarget === 'vk' ? vkClear : rawClear}
                  testid="inv-keypad" />
                {keypadTarget === 'raw' && (
                  <button type="button" data-testid="keypad-raw-parse" onClick={() => parsePrefill()}
                    className="w-full mt-2 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium active:bg-indigo-700">EAN erkennen</button>
                )}
              </div>
            )}
            <div className="flex gap-2 pt-2 pb-5 -mb-5 sticky bottom-0 bg-card">
              <Button data-testid="dialog-cancel" variant="outline" className="flex-1" onClick={closeDialog} disabled={submitting}>Abbrechen</Button>
              <Button data-testid="dialog-submit" className="flex-1" onClick={submitDialog} disabled={submitting}>{submitting ? 'Speichert…' : 'Übernehmen'}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Lösch-Bestätigung für eine beliebige Erfassung */}
      {delEntry && (
        <div className="fixed inset-0 z-[10000] bg-black/50 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="delete-dialog">
          <div className="bg-card rounded-2xl shadow-2xl w-full max-w-sm my-auto max-h-[90dvh] overflow-y-auto p-5 space-y-3">
            <h3 className="font-bold text-foreground">Erfassung wirklich löschen?</h3>
            <p className="text-sm text-foreground" data-testid="delete-dialog-detail">
              {(delEntry.qty || 1) > 1
                ? `Diese Erfassung mit ${delEntry.qty} Exemplaren entfernen?`
                : 'Diese Erfassung (1 Exemplar) entfernen?'}
            </p>
            <div className="rounded-lg bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{eur(delEntry.vk_cents)}</span>
              {' · '}{delEntry.tax_rate ? delEntry.tax_rate + '%' : 'Steuer?'}
              {' · '}{delEntry.qty}× {delEntry.source}
              {delEntry.raw_scan ? <span className="block break-all mt-1">Barcode: {delEntry.raw_scan}</span> : null}
            </div>
            <p className="text-[11px] text-muted-foreground">Die Erfassung wird nachvollziehbar storniert (nicht endgültig gelöscht) und aus den aktiven Summen entfernt.</p>
            <div className="flex gap-2 pt-1">
              <Button data-testid="delete-cancel-btn" autoFocus variant="outline" className="flex-1" onClick={closeDeleteDialog} disabled={deleting}>Nein</Button>
              <Button data-testid="delete-confirm-btn" className="flex-1 bg-red-600 hover:bg-red-700" onClick={confirmDelete} disabled={deleting}>{deleting ? 'Löscht…' : 'Ja, löschen'}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Bestätigung: abgeschlossene Inventur wieder öffnen */}
      {reopenOpen && (
        <div className="fixed inset-0 z-[10000] bg-black/50 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="reopen-dialog">
          <div className="bg-card rounded-2xl shadow-2xl w-full max-w-sm my-auto max-h-[90dvh] overflow-y-auto p-5 space-y-3">
            <h3 className="font-bold text-foreground">Abgeschlossene Inventur wirklich zur Bearbeitung wieder öffnen?</h3>
            <p className="text-sm text-muted-foreground">
              Der Status wechselt zurück auf <span className="font-medium text-foreground">Entwurf</span>. Bestehende Erfassungen und Stornos bleiben unverändert erhalten; Korrektur und Erfassung sind wieder möglich und die Inventur kann danach erneut abgeschlossen werden.
            </p>
            <div className="flex gap-2 pt-1">
              <Button data-testid="reopen-cancel-btn" autoFocus variant="outline" className="flex-1" onClick={() => { if (!reopening) setReopenOpen(false); }} disabled={reopening}>Nein</Button>
              <Button data-testid="reopen-confirm-btn" className="flex-1 bg-amber-600 hover:bg-amber-700" onClick={confirmReopen} disabled={reopening}>{reopening ? 'Öffnet…' : 'Ja, wieder öffnen'}</Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-card border border-border rounded-xl px-4 py-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-bold text-foreground">{value}</p>
    </div>
  );
}
